import {useCallback, useEffect, useMemo, useState} from "react";
import {doFetch} from "../../../util/fetch";
import {useServerId} from "../useServerId";
import {usePlayers} from "../players/PlayerContext";
import {GetPlayersResponse, PlayerDetails} from "../interfaces";
import {PermissionPlayer} from "./interfaces";

const PAGE_SIZE = 50;

function toPermissionPlayer(p: PlayerDetails): PermissionPlayer {
    return {steamId: p.steamId, displayName: p.displayName};
}

// Retrieves the server's known players using the exact same sources as the Players page:
// online players from the live PlayerContext, offline players from the paged /api/client/players
// endpoint, and server-side search via /api/client/players/search. `query` drives the search;
// when empty, the combined online + paged offline list is returned.
export function usePlayerDirectory(query: string) {
    const serverId = useServerId();
    const players = usePlayers();

    const [offline, setOffline] = useState<PlayerDetails[]>([]);
    const [pageInfo, setPageInfo] = useState<{ page: number, hasNextPage: boolean }>({page: 0, hasNextPage: false});
    const [isLoading, setIsLoading] = useState(false);

    const [searchResults, setSearchResults] = useState<PermissionPlayer[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showingSearch, setShowingSearch] = useState(false);

    const fetchPage = useCallback(async (page: number) => {
        setIsLoading(true);
        const res = await doFetch<GetPlayersResponse>(`/api/client/players?serverId=${serverId}&pageSize=${PAGE_SIZE}&page=${page}&currentWipe=false`);
        setIsLoading(false);
        if (res.data) {
            const data = res.data;
            setOffline(prev => page === 0 ? data.players : prev.concat(data.players));
            setPageInfo({page, hasNextPage: data.hasNextPage});
        }
    }, [serverId]);

    useEffect(() => {
        fetchPage(0);
    }, [fetchPage]);

    const trimmed = query.trim();

    useEffect(() => {
        if (!trimmed) {
            setShowingSearch(false);
            return;
        }
        const field = /^\d+$/.test(trimmed) ? "steamId" : "displayName";
        const timer = setTimeout(async () => {
            setIsSearching(true);
            setShowingSearch(true);
            const res = await doFetch<GetPlayersResponse>(
                `/api/client/players/search?serverId=${serverId}&q=${encodeURIComponent(trimmed)}&field=${field}&limit=${PAGE_SIZE}`
            );
            setIsSearching(false);
            if (res.data) {
                setSearchResults(res.data.players.map(toPermissionPlayer));
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [trimmed, serverId]);

    const list = useMemo(() => {
        if (showingSearch) {
            return searchResults;
        }
        const map = new Map<string, PermissionPlayer>();
        offline.forEach(p => map.set(p.steamId, toPermissionPlayer(p)));
        players.online.forEach(p => map.set(p.steamId, toPermissionPlayer(p)));
        return Array.from(map.values());
    }, [showingSearch, searchResults, offline, players.online]);

    const loadMore = useCallback(() => {
        if (!showingSearch && pageInfo.hasNextPage && !isLoading) {
            fetchPage(pageInfo.page + 1);
        }
    }, [showingSearch, pageInfo, isLoading, fetchPage]);

    return {
        players: list,
        loadMore,
        hasNextPage: pageInfo.hasNextPage && !showingSearch,
        isLoading: isLoading || isSearching
    };
}
